import { useMemo } from 'react';
import { isPast } from 'date-fns';
import { User, AlertCircle, Calendar, ChevronRight } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useUIStore } from '@/stores/ui-store';
import { formatDisplayDate } from '@/lib/date-format';
import { cn, getInitials, getContrastTextColor, getPriorityHexColor } from '@/lib/utils';
import { registerView, type ViewProps } from '@/components/workspace/ViewRegistry';
import type { Task, TaskStatus } from '@/types';
import { getTaskAssignees } from '@/lib/task-assignees';

// Priority colors now use getPriorityHexColor for solid fills

interface AssigneeGroup {
  id: string | null;
  name: string;
  avatarUrl?: string | null;
  tasks: Task[];
}

interface WorkloadTaskProps {
  task: Task;
  status: TaskStatus | undefined;
  onClick: () => void;
}

function WorkloadTask({ task, status, onClick }: WorkloadTaskProps) {
  const isOverdue = task.dueDate && isPast(new Date(task.dueDate)) && !status?.isDoneState;

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2 rounded-md text-left',
        'hover:bg-muted/50 transition-colors group',
        'focus:outline-none focus:bg-muted/50'
      )}
    >
      {/* Priority indicator */}
      <div
        className="h-2 w-2 rounded-full shrink-0"
        style={{ backgroundColor: getPriorityHexColor(task.priority) }}
      />

      {/* Task title */}
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span className="truncate">{task.title}</span>
        {isOverdue && (
          <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
        )}
      </div>

      {/* Status badge */}
      {status && (() => {
        const sBg = status.color || '#6b7280';
        const sText = getContrastTextColor(sBg);
        return (
          <Badge
            variant="secondary"
            className="text-xs shrink-0 font-medium"
            style={{ backgroundColor: sBg, color: sText, borderColor: 'transparent' }}
          >
            {status.name}
          </Badge>
        );
      })()}

      {/* Due date */}
      {task.dueDate && (
        <span
          className={cn(
            'text-xs shrink-0 flex items-center gap-1',
            isOverdue ? 'text-destructive' : 'text-muted-foreground'
          )}
        >
          <Calendar className="h-3 w-3" />
          {formatDisplayDate(task.dueDate)}
        </span>
      )}

      <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
    </button>
  );
}

interface WorkloadGroupProps {
  group: AssigneeGroup;
  statuses: TaskStatus[];
  onTaskClick: (taskId: string) => void;
}

function WorkloadGroup({ group, statuses, onTaskClick }: WorkloadGroupProps) {
  const overdueCount = group.tasks.filter((task) => {
    const status = statuses.find((s) => s.id === task.statusId);
    return task.dueDate && isPast(new Date(task.dueDate)) && !status?.isDoneState;
  }).length;

  const getStatus = (statusId: string) => statuses.find((s) => s.id === statusId);

  // Calculate workload bar (simple: based on task count, max 10 tasks = full)
  const workloadPercent = Math.min((group.tasks.length / 10) * 100, 100);
  const workloadColor =
    workloadPercent > 80
      ? 'bg-destructive'
      : workloadPercent > 50
        ? 'bg-warning'
        : 'bg-primary';

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      {/* Group header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-muted/30 border-b border-border">
        {group.id ? (
          <Avatar className="h-8 w-8">
            <AvatarImage src={group.avatarUrl || undefined} />
            <AvatarFallback className="text-xs">
              {getInitials(group.name)}
            </AvatarFallback>
          </Avatar>
        ) : (
          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
            <User className="h-4 w-4 text-muted-foreground" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{group.name}</span>
            <Badge variant="secondary" className="text-xs">
              {group.tasks.length} task{group.tasks.length !== 1 ? 's' : ''}
            </Badge>
            {overdueCount > 0 && (
              <Badge variant="destructive" className="text-xs gap-1">
                <AlertCircle className="h-3 w-3" />
                {overdueCount} overdue
              </Badge>
            )}
          </div>

          {/* Workload bar */}
          <div className="mt-1.5 h-1 w-full max-w-32 bg-muted rounded-full overflow-hidden">
            <div
              className={cn('h-full rounded-full transition-all', workloadColor)}
              style={{ width: `${workloadPercent}%` }}
            />
          </div>
        </div>
      </div>

      {/* Tasks */}
      <div className="p-2 space-y-1">
        {group.tasks.length === 0 ? (
          <p className="text-sm text-muted-foreground px-3 py-2">
            No tasks assigned
          </p>
        ) : (
          group.tasks.map((task) => (
            <WorkloadTask
              key={task.id}
              task={task}
              status={getStatus(task.statusId)}
              onClick={() => onTaskClick(task.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function WorkloadView({ tasks, statuses, members, isLoading, isClient }: ViewProps) {
  const { openTaskPanel } = useUIStore();
  const doneStatusIds = useMemo(
    () => new Set(statuses.filter((s) => s.isDoneState).map((s) => s.id)),
    [statuses],
  );
  const activeTasks = useMemo(
    () => tasks.filter((task) => !doneStatusIds.has(task.statusId)),
    [tasks, doneStatusIds],
  );

  const assigneeGroups = useMemo(() => {
    const groups: Map<string | null, AssigneeGroup> = new Map();

    // Initialize with all members (excluding clients from workload view)
    const internalMembers = members.filter((m) => m.role !== 'client' && m.user);
    
    internalMembers.forEach((member) => {
      groups.set(member.userId, {
        id: member.userId,
        name: member.user.displayName,
        avatarUrl: member.user.avatarUrl,
        tasks: [],
      });
    });

    // Always have an "Unassigned" group
    groups.set(null, {
      id: null,
      name: 'Unassigned',
      avatarUrl: null,
      tasks: [],
    });

    // Distribute tasks to groups
    // For clients, only show client-visible tasks
    const visibleTasks = isClient
      ? activeTasks.filter((t) => t.isClientVisible)
      : activeTasks;

    visibleTasks.forEach((task) => {
      const userAssignees = getTaskAssignees(task);
      if (userAssignees.length > 0) {
        let placedInMemberColumn = false;
        for (const u of userAssignees) {
          const group = groups.get(u.id);
          if (group) {
            group.tasks.push(task);
            placedInMemberColumn = true;
          }
        }
        if (!placedInMemberColumn) {
          const unassigned = groups.get(null);
          if (unassigned) unassigned.tasks.push(task);
        }
      } else {
        const unassigned = groups.get(null);
        if (unassigned) unassigned.tasks.push(task);
      }
    });

    // Sort tasks within each group by priority and position
    const priorityOrder: Record<string, number> = {
      urgent: 0,
      high: 1,
      medium: 2,
      low: 3,
    };

    groups.forEach((group) => {
      group.tasks.sort((a, b) => {
        const priorityDiff =
          (priorityOrder[a.priority] ?? 4) - (priorityOrder[b.priority] ?? 4);
        if (priorityDiff !== 0) return priorityDiff;
        return a.position - b.position;
      });
    });

    // Convert to array and sort: most tasks first, unassigned last
    const result = Array.from(groups.values());
    result.sort((a, b) => {
      if (a.id === null) return 1;
      if (b.id === null) return -1;
      return b.tasks.length - a.tasks.length;
    });

    return result;
  }, [activeTasks, members, isClient]);

  const handleTaskClick = (taskId: string) => {
    openTaskPanel(taskId);
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (activeTasks.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
        <p>No active tasks in this list</p>
        <p className="text-sm text-muted-foreground/70">
          Create tasks to see workload distribution
        </p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-full">
      <div className="p-4 grid gap-4 grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
        {assigneeGroups.map((group) => (
          <WorkloadGroup
            key={group.id ?? 'unassigned'}
            group={group}
            statuses={statuses}
            onTaskClick={handleTaskClick}
          />
        ))}
      </div>
    </ScrollArea>
  );
}

registerView({
  id: 'workload',
  label: 'Workload',
  icon: 'Users',
  component: WorkloadView,
});

export { WorkloadView };
